import { z } from "zod";
import { consultaPaginadaSchema, paginaSchema } from "../common/pagination";

/**
 * Contrato do dominio de referencias (posts salvos do Instagram).
 */

export const tipoDeMediaSchema = z.enum([
  "image",
  "video",
  "carousel",
  "unknown",
]);

export const colecaoSchema = z.object({
  id: z.string().min(1),
  nome: z.string(),
  tipo: z.string().nullable(),
  mediaCount: z.number().int().nullable(),
  atualizadoEm: z.string().datetime(),
});

export const referenciaSchema = z.object({
  id: z.string().min(1),
  collectionId: z.string().min(1),
  collectionName: z.string(),
  username: z.string().nullable(),
  fullName: z.string().nullable(),
  caption: z.string().nullable(),
  imageUrl: z.string().nullable(),
  images: z.array(z.string()),
  videoUrl: z.string().nullable(),
  permalink: z.string().nullable(),
  shortcode: z.string().nullable(),
  mediaType: tipoDeMediaSchema,
  takenAt: z.string().datetime().nullable(),
  likeCount: z.number().int().nullable(),
  commentCount: z.number().int().nullable(),
  shareCount: z.number().int().nullable(),
  saveCount: z.number().int().nullable(),
  isAd: z.boolean(),
  favorito: z.boolean(),
  ocultoEm: z.string().datetime().nullable(),
  prescricao: z.string().nullable(),
  tema: z.string().nullable(),
  gancho: z.string().nullable(),
  cta: z.string().nullable(),
  transcicao: z.string().nullable(),
  clickupTaskId: z.string().nullable(),
  syncedAt: z.string().datetime(),
});

export const ordenacaoDeStoriesSchema = z.enum(["recente", "antigo", "perfil"]);
export const ordenacaoDaGaleriaSchema = z.enum([
  "recente",
  "antigo",
  "perfil",
  "curtidas",
  "comentarios",
]);

export const listarReferenciasQuerySchema = consultaPaginadaSchema
  .omit({ desde: true, ate: true })
  .extend({
    collectionId: z.string().min(1).optional(),
    q: z.string().trim().min(1).optional(),
    ordenar: ordenacaoDaGaleriaSchema.optional(),
    /** true = só anúncios; false = só orgânicos; omitido = todos. */
    isAd: z
      .enum(["true", "false"])
      .transform((valor) => valor === "true")
      .optional(),
    favoritos: z
      .enum(["true", "1"])
      .transform(() => true)
      .optional(),
  });

export const paginaDeReferenciasSchema = paginaSchema(referenciaSchema);

export const statusSincronizacaoSchema = z.enum([
  "idle",
  "running",
  "success",
  "error",
]);

export const estadoSincronizacaoSchema = z.object({
  status: statusSincronizacaoSchema,
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  message: z.string().nullable(),
  progressPercent: z.number().int().min(0).max(100).nullable(),
  logs: z.array(z.string()),
  running: z.boolean(),
});

/** Modo do sync manual de coleções. */
export const modoSincronizacaoReferenciasSchema = z.enum(["novos", "completo"]);

export const iniciarSincronizacaoSchema = z
  .object({
    modo: modoSincronizacaoReferenciasSchema.default("completo"),
  })
  .strict()
  .default({ modo: "completo" });

export const iniciarSincronizacaoRespostaSchema = z.object({
  ok: z.literal(true),
  estado: estadoSincronizacaoSchema,
});

export const colecaoDisponivelSchema = z.object({
  id: z.string().min(1),
  nome: z.string(),
  tipo: z.string().nullable(),
  mediaCount: z.number().int().nullable(),
  selecionada: z.boolean(),
});

export const salvarColecoesSelecionadasSchema = z.object({
  ids: z.array(z.string().min(1)),
});

export const salvarColecoesSelecionadasRespostaSchema = z.object({
  ok: z.literal(true),
  ids: z.array(z.string().min(1)),
});

export const contaSeguidaSchema = z.object({
  id: z.string().min(1),
  username: z.string().min(1),
  fullName: z.string().nullable(),
  profilePicUrl: z.string().nullable(),
  isPrivate: z.boolean(),
  isVerified: z.boolean(),
  selecionada: z.boolean(),
});

export const salvarContasStoriesSelecionadasSchema = z.object({
  ids: z.array(z.string().min(1)),
});

export const salvarContasStoriesSelecionadasRespostaSchema = z.object({
  ok: z.literal(true),
  ids: z.array(z.string().min(1)),
});

export const tipoConteudoCompartilhadoStorySchema = z.enum(["post", "reel"]);

export const storySchema = z.object({
  id: z.string().min(1),
  contaId: z.string().min(1),
  username: z.string().nullable(),
  fullName: z.string().nullable(),
  profilePicUrl: z.string().nullable(),
  caption: z.string().nullable(),
  mediaType: tipoDeMediaSchema,
  imageUrl: z.string().nullable(),
  images: z.array(z.string()),
  videoUrl: z.string().nullable(),
  takenAt: z.string().datetime().nullable(),
  syncedAt: z.string().datetime(),
  expiraEm: z.string().datetime().nullable(),
  conteudoCompartilhadoUrl: z.string().url().nullable(),
  conteudoCompartilhadoTipo: tipoConteudoCompartilhadoStorySchema.nullable(),
  favorito: z.boolean(),
  ocultoEm: z.string().datetime().nullable(),
});

export const listarStoriesQuerySchema = consultaPaginadaSchema
  .omit({ desde: true, ate: true })
  .extend({
    contaIds: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .transform((valor) => {
        if (valor === undefined) return undefined;
        const lista = Array.isArray(valor) ? valor : valor.split(",");
        const limpos = lista.map((item) => item.trim()).filter(Boolean);
        return limpos.length > 0 ? limpos : undefined;
      }),
    q: z.string().trim().min(1).optional(),
    mediaType: z.enum(["image", "video"]).optional(),
    ordenar: ordenacaoDeStoriesSchema.optional(),
    favoritos: z
      .enum(["true", "1"])
      .transform(() => true)
      .optional(),
  });

export const paginaDeStoriesSchema = paginaSchema(storySchema);

export const contaStoriesMonitoradaSchema = z.object({
  id: z.string().min(1),
  username: z.string().min(1),
  fullName: z.string().nullable(),
  profilePicUrl: z.string().nullable(),
  ultimaAtualizacaoEm: z.string().datetime().nullable(),
});

export const statusInstagramSessaoSchema = z.enum([
  "valida",
  "expirada",
  "checkpoint",
  "bloqueada",
  "ausente",
]);

export const instagramSessaoSchema = z.object({
  username: z.string().nullable(),
  profilePicUrl: z.string().nullable(),
  deviceId: z.string().nullable(),
  status: statusInstagramSessaoSchema,
  validadaEm: z.string().datetime().nullable(),
  expiraEm: z.string().datetime().nullable(),
  ultimoLoginEm: z.string().datetime().nullable(),
  ultimoErro: z.string().nullable(),
  breakerAte: z.string().datetime().nullable(),
});

export const invalidarInstagramSessaoRespostaSchema = z.object({
  ok: z.literal(true),
});

export const conectarInstagramSchema = z.object({
  username: z.string().trim().min(1).max(120),
  password: z.string().min(1).max(200),
});

export const confirmar2faInstagramSchema = z.object({
  desafioId: z.string().uuid(),
  codigo: z.string().trim().regex(/^\d{6}$/),
});

export const conectarInstagramRespostaSchema = z.discriminatedUnion("status", [
  z.object({
    ok: z.literal(true),
    status: z.literal("conectada"),
    sessao: instagramSessaoSchema,
  }),
  z.object({
    ok: z.literal(true),
    status: z.literal("precisa_2fa"),
    desafioId: z.string().uuid(),
  }),
]);

export const comentarioReferenciaSchema = z.object({
  id: z.string().min(1),
  postId: z.string().min(1),
  texto: z.string().min(1),
  autorNome: z.string().min(1),
  clickupCommentId: z.string().nullable(),
  criadoEm: z.string().datetime(),
  atualizadoEm: z.string().datetime(),
  podeEditar: z.boolean(),
});

export const criarComentarioReferenciaSchema = z.object({
  texto: z.string().trim().min(1).max(8_000),
  autorNome: z.string().trim().min(1, "informe seu nome").max(80),
});

export const atualizarComentarioReferenciaSchema = z.object({
  texto: z.string().trim().min(1).max(8_000),
});

export const excluirComentarioReferenciaRespostaSchema = z.object({
  ok: z.literal(true),
});

/** Status das integracoes (sem segredos). */
export const statusIntegracoesSchema = z.object({
  clickup: z.object({
    tokenConfigurado: z.boolean(),
    listIdMascarado: z.string().nullable(),
    webhookSecretConfigurado: z.boolean(),
    customItemIdConfigurado: z.boolean(),
    camposCustomizados: z.number().int().min(0),
    /** Credenciais editaveis na conta do usuario (nao so env). */
    editavelNaConta: z.boolean(),
  }),
});

/** Atualiza token/listId ClickUp da conta do usuario. token vazio limpa. */
export const atualizarClickUpSchema = z
  .object({
    token: z.string().max(500).optional(),
    listId: z.string().trim().max(64).optional(),
  })
  .strict();

export const atualizarFlagsGaleriaSchema = z
  .object({
    favorito: z.boolean().optional(),
    oculto: z.boolean().optional(),
  })
  .strict()
  .refine(
    (corpo) => corpo.favorito !== undefined || corpo.oculto !== undefined,
    { message: "Informe favorito e/ou oculto" },
  );

export type TipoDeMedia = z.infer<typeof tipoDeMediaSchema>;
export type Colecao = z.infer<typeof colecaoSchema>;
export type Referencia = z.infer<typeof referenciaSchema>;
export type OrdenacaoDeStories = z.infer<typeof ordenacaoDeStoriesSchema>;
export type OrdenacaoDaGaleria = z.infer<typeof ordenacaoDaGaleriaSchema>;
export type ListarReferenciasQuery = z.infer<typeof listarReferenciasQuerySchema>;
export type PaginaDeReferencias = z.infer<typeof paginaDeReferenciasSchema>;
export type StatusSincronizacao = z.infer<typeof statusSincronizacaoSchema>;
export type EstadoSincronizacao = z.infer<typeof estadoSincronizacaoSchema>;
export type ModoSincronizacaoReferencias = z.infer<
  typeof modoSincronizacaoReferenciasSchema
>;
export type IniciarSincronizacao = z.infer<typeof iniciarSincronizacaoSchema>;
export type IniciarSincronizacaoResposta = z.infer<
  typeof iniciarSincronizacaoRespostaSchema
>;
export type ColecaoDisponivel = z.infer<typeof colecaoDisponivelSchema>;
export type SalvarColecoesSelecionadas = z.infer<
  typeof salvarColecoesSelecionadasSchema
>;
export type SalvarColecoesSelecionadasResposta = z.infer<
  typeof salvarColecoesSelecionadasRespostaSchema
>;
export type ContaSeguida = z.infer<typeof contaSeguidaSchema>;
export type SalvarContasStoriesSelecionadas = z.infer<
  typeof salvarContasStoriesSelecionadasSchema
>;
export type SalvarContasStoriesSelecionadasResposta = z.infer<
  typeof salvarContasStoriesSelecionadasRespostaSchema
>;
export type Story = z.infer<typeof storySchema>;
export type ListarStoriesQuery = z.infer<typeof listarStoriesQuerySchema>;
export type PaginaDeStories = z.infer<typeof paginaDeStoriesSchema>;
export type ContaStoriesMonitorada = z.infer<
  typeof contaStoriesMonitoradaSchema
>;
export type AtualizarFlagsGaleria = z.infer<typeof atualizarFlagsGaleriaSchema>;
export type StatusInstagramSessao = z.infer<typeof statusInstagramSessaoSchema>;
export type InstagramSessao = z.infer<typeof instagramSessaoSchema>;
export type InvalidarInstagramSessaoResposta = z.infer<
  typeof invalidarInstagramSessaoRespostaSchema
>;
export type ConectarInstagram = z.infer<typeof conectarInstagramSchema>;
export type Confirmar2faInstagram = z.infer<typeof confirmar2faInstagramSchema>;
export type ConectarInstagramResposta = z.infer<
  typeof conectarInstagramRespostaSchema
>;
export type ComentarioReferencia = z.infer<typeof comentarioReferenciaSchema>;
export type CriarComentarioReferencia = z.infer<
  typeof criarComentarioReferenciaSchema
>;
export type AtualizarComentarioReferencia = z.infer<
  typeof atualizarComentarioReferenciaSchema
>;
export type ExcluirComentarioReferenciaResposta = z.infer<
  typeof excluirComentarioReferenciaRespostaSchema
>;
export type StatusIntegracoes = z.infer<typeof statusIntegracoesSchema>;
export type AtualizarClickUp = z.infer<typeof atualizarClickUpSchema>;
